import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import jwtDecode from "jwt-decode";

import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import CustomTable from "../../../components/table/table";

import {
  adminBulkEmailDetail,
  superadminBulkEmailDetail,
  superadminBulkEmailApprove,
} from "../admin.api";

export default function BulkEmailDetail(props) {
  const { uuid } = useParams();
  const [bulkEmail, setBulkEmail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [recipientsPerPage, setRecipientsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("user");

  const [isActive, setIsActive] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const adminType = Number(jwtDecode(props.state.payload).user_type);
  const baseApiUrl = process.env.REACT_APP_BASE_URL || "";
  const fileBaseUrl = baseApiUrl.endsWith("/api")
    ? baseApiUrl.slice(0, -4) + "/storage"
    : baseApiUrl.replace("/api", "") + "storage";

  const fetchDetail = (page = 1) => {
    if (!uuid) {
      setIsActive(false);
      return;
    }
    setIsActive(true);

    const apiCall =
      adminType === 2
        ? superadminBulkEmailDetail(uuid, page)
        : adminBulkEmailDetail(uuid, page);

    apiCall
      .then((res) => {
        if (res?.success && res.data) {
          const bulk = res.data.bulk_email || null;
          const recipientPager = res.data.recipients || {};
          const recipientData = recipientPager.data || [];
          setBulkEmail(bulk);
          setRecipients(recipientData);
          setRecipientsPage(recipientPager.current_page || page);
          setRecipientsTotalPages(recipientPager.last_page || 1);
        } else {
          setBulkEmail(null);
          setRecipients([]);
          setSnackMessage("Failed to load bulk email details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching bulk email detail:", error);
        setBulkEmail(null);
        setRecipients([]);
        setSnackMessage("Failed to load bulk email details");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  useEffect(() => {
    fetchDetail(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag && !bulkEmail) {
      props.history.push("/bulk-emails");
    }
  };

  const handleRecipientsPageChange = (page) => {
    if (page < 1 || page > recipientsTotalPages) return;
    fetchDetail(page);
  };

  const renderRecipientStatus = (status) => {
    const raw = status || "";
    const value = String(raw).toLowerCase();
    let label = raw || "--";
    let cls = "badge bg-secondary";

    if (["sent", "delivered", "success", "successful"].includes(value)) {
      label = "Sent";
      cls = "badge bg-success";
    } else if (["pending", "queued", "processing", "submitted"].includes(value)) {
      label = "Submitted";
      cls = "badge bg-warning text-dark";
    } else if (["failed", "bounced", "error"].includes(value)) {
      label = "Failed";
      cls = "badge bg-danger";
    } else if (["opened", "read"].includes(value)) {
      label = "Opened";
      cls = "badge bg-info text-dark";
    }

    return <span className={`${cls} rounded-0`}>{label}</span>;
  };

  const renderRecipientsPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, recipientsPage - Math.floor(maxVisible / 2));
    let end = Math.min(recipientsTotalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(
        <button key={1} className="btn btn-sm btn-outline-primary" onClick={() => handleRecipientsPageChange(1)}>1</button>
      );
      if (start > 2) pages.push(<span key="start-dots" className="px-1 text-primary">...</span>);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${recipientsPage === i ? "btn-primary text-white" : "btn-outline-primary"}`}
          onClick={() => handleRecipientsPageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (end < recipientsTotalPages) {
      if (end < recipientsTotalPages - 1) pages.push(<span key="end-dots" className="px-1 text-primary">...</span>);
      pages.push(
        <button key={recipientsTotalPages} className="btn btn-sm btn-outline-primary" onClick={() => handleRecipientsPageChange(recipientsTotalPages)}>{recipientsTotalPages}</button>
      );
    }

    return pages;
  };

  const handleRecipientsPerPageChange = (e) => {
    setRecipientsPerPage(parseInt(e.target.value));
    setRecipientsPage(1);
  };

  const attachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return "#";
    // Replace escaped slashes with normal slashes and construct URL
    const cleanPath = attachmentPath.replace(/\\/g, '/');
    return `${fileBaseUrl}/${cleanPath}`;
  };

  const handleApprove = () => {
    if (!uuid || adminType !== 2) return;
    setIsActive(true);
    superadminBulkEmailApprove(uuid)
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email approved successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        fetchDetail(recipientsPage);
      })
      .catch((error) => {
        setSnackMessage(error?.message || "Failed to approve bulk email");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  return (
    <div>
      <Loader open={isActive} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="view-container">
        <span className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 pt-2">
            <b>Bulk Email Details</b>
          </h6>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => props.history.push("/bulk-emails")}
          >
            Back
          </button>
        </span>
        <hr />

        {!bulkEmail && !isActive && (
          <div className="alert alert-warning">Details not available.</div>
        )}

        {bulkEmail && (
          <>
            <ul className="nav nav-tabs border-0" style={{ borderBottom: "none" }}>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "user" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("user")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "user" ? "3px solid #0d6efd" : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Message Details
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "recipients" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("recipients")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "recipients"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Recipient Details
                </button>
              </li>
            </ul>

            {activeTab === "user" && (
              <div className="pt-3">
                <div className="data">
                  <div className="row">
                    <div className="col-3">Subject</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.subject}</div>
                  </div>
                  <div className="row align-items-start">
                    <div className="col-3">Body</div>
                    <div className="col-1">:</div>
                    <div className="col-7">
                      <div
                        className="border rounded p-2"
                        dangerouslySetInnerHTML={{ __html: bulkEmail.body_html }}
                      />
                    </div>
                  </div>
                  {Array.isArray(bulkEmail.attachments) && bulkEmail.attachments.length > 0 && (
                    <div className="row align-items-start">
                      <div className="col-3">Attachments</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">
                        <div className="d-flex flex-column gap-1">
                          {bulkEmail.attachments.map((att, index) => (
                            <div key={`${att.path}-${index}`}>
                              <a href={attachmentUrl(att.path)} target="_blank" rel="noreferrer">
                                {att.name}
                              </a>
                              <small className="text-muted ms-2">
                                ({att.mime}, {att.size} bytes)
                              </small>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {bulkEmail.review_message && (
                    <div className="row align-items-start">
                      <div className="col-3">Review Message</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">{bulkEmail.review_message}</div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-3">User Category</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.user_category}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Status</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.status}</div>
                  </div>
                  {bulkEmail.metadata?.campaign && (
                    <div className="row">
                      <div className="col-3">Campaign</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">{bulkEmail.metadata.campaign}</div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-3">Priority</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.metadata?.priority ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Created At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.created_at}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Submitted At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.submitted_at ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Approved At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.approved_at ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Total Recipients</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.total_recipients}</div>
                  </div>
                  {/* <div className="row">
                    <div className="col-3">Sent</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.sent_recipients}</div>
                  </div> */}
                  {bulkEmail.creator && (
                    <>
                      <div className="row">
                        <div className="col-3">Creator Name</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">{bulkEmail.creator.name}</div>
                      </div>
                      <div className="row">
                        <div className="col-3">Creator Email</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">{bulkEmail.creator.email}</div>
                      </div>
                    </>
                  )}
                </div>

                {adminType === 2 && bulkEmail?.status !== "sent" && bulkEmail?.status !== "sending" && bulkEmail?.status !== "queued" && (
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-success px-4"
                      onClick={handleApprove}
                      disabled={isActive}
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "recipients" && (
              <div className="pt-3">
                <div className="mb-3">
                  <h6>
                    <b>Recipients</b>
                  </h6>
                  <CustomTable
                    columns={[
                      { title: "Name", field: "name" },
                      { title: "Recipient Type", field: "recipient_type" },
                      { title: "Email", field: "email" },
                      {
                        title: "Status",
                        render: (row) => renderRecipientStatus(row.send_status)
                      }
                    ]}
                    data={recipients}
                    paging={false}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="flex-grow-1"></div>
                    <div className="btn-group">
                      <button className="btn btn-sm btn-outline-primary" disabled={recipientsPage === 1} onClick={() => handleRecipientsPageChange(recipientsPage - 1)}>Prev</button>
                      {renderRecipientsPagination()}
                      <button className="btn btn-sm btn-outline-primary" disabled={recipientsPage === recipientsTotalPages} onClick={() => handleRecipientsPageChange(recipientsPage + 1)}>Next</button>
                    </div>
                    <div className="flex-grow-1 d-flex justify-content-end">
                      <select className="form-control form-control-sm w-auto" value={recipientsPerPage} onChange={handleRecipientsPerPageChange} style={{ height: "31px", fontSize: "8px" }}>
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
